import { FC, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

/**
 * D18 (2026-08-10) — catch Supabase recovery links that land on the wrong route.
 *
 * THE FAILURE THIS EXISTS FOR
 * `/reset-password` has existed and worked since May. What broke in production
 * is upstream of the app: if `redirectTo` is not in the Supabase project's
 * "Redirect URLs" allowlist, Supabase **silently ignores it and falls back to
 * the Site URL** — so the caregiver lands on the marketing landing page with
 * `#error=otp_expired&error_code=...` in the hash and no idea what happened.
 *
 * That is a dashboard setting, not a code bug, and the real fix is the Auth URL
 * configuration (see docs/plans/2026-08-10-password-reset-p0.md). But a
 * misconfigured allowlist should not silently strand someone trying to get back
 * into their account — especially a caregiver locked out of a senior's safety
 * dashboard.
 *
 * So: any route that receives recovery markers hands them to /reset-password,
 * hash and query intact, which already knows how to render both the success and
 * the expired cases. If the allowlist is right this never fires.
 *
 * Deliberately narrow. It only reacts to `type=recovery` or an explicit auth
 * error hash, and it never touches `/reset-password` itself (which would loop).
 */
const isRecoveryHash = (hash: string): boolean => {
  if (!hash) return false;
  // Implicit flow: #access_token=...&type=recovery
  if (hash.includes('type=recovery')) return true;
  // Supabase rejected the token before redirecting: #error=...&error_code=otp_expired
  // Only treat it as ours if it names an auth error we own — a stray `error=`
  // from some other feature must not hijack navigation.
  if (/error_code=(otp_expired|access_denied)/.test(hash)) return true;
  if (/error=access_denied/.test(hash)) return true;
  return false;
};

export const RecoveryLinkCatcher: FC = () => {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (location.pathname.startsWith('/reset-password')) return;

    const hash = window.location.hash || '';
    const search = window.location.search || '';
    const hashIsRecovery = isRecoveryHash(hash);

    if (!hashIsRecovery) return;

    // Preserve BOTH: PKCE puts `?code=` in the query, the implicit and error
    // shapes put everything in the hash. ResetPassword reads both.
    console.warn(
      '[RecoveryLinkCatcher] Recovery link landed on %s instead of /reset-password. ' +
        'This means the Supabase Auth redirect allowlist is missing the reset URL — ' +
        'see docs/plans/2026-08-10-password-reset-p0.md. Forwarding so the caregiver is not stranded.',
      location.pathname
    );
    navigate(`/reset-password${search}${hash}`, { replace: true });
  }, [location.pathname, navigate]);

  return null;
};

export default RecoveryLinkCatcher;
